import { z } from 'zod';
// ---------------------------------------------------------------------------
// Generative layout spec envelope (design §5.9, §7.6).
//
// Three separated concerns:
//   - presentation primitives  -> bounded, engineered (packages/catalog)
//   - data sources             -> bounded, engineered (api adapters)
//   - section semantics        -> UNBOUNDED, agent-supplied custom context
//
// The inner json-render spec ({ root, elements, ... }) is unversioned upstream,
// so we wrap it in our own envelope and validate the inner spec with json-render's
// validateSpec() against our catalog at the MCP write boundary (not here — here we
// only type the envelope + the section-semantics sidecar). App code must import
// the renderer through @almanac/catalog, never @json-render/* directly.
// ---------------------------------------------------------------------------
export const LAYOUT_SPEC_VERSION = 1;
/**
 * Unbounded, agent-supplied meaning for a section/region of the dashboard.
 * "Meaning is data on the spec, not an engineered type" — new section kinds
 * require no engineering, only new values here.
 */
export const SectionSemanticsSchema = z
    .object({
    id: z.string().min(1),
    title: z.string().optional(),
    /** what this section is for, in the agent's own words */
    intent: z.string().optional(),
    /** free-form hint about cadence / freshness / source, e.g. "hourly", "on demand" */
    refresh: z.string().optional(),
    /** arbitrary additional context the agent wants to attach */
    context: z.record(z.string(), z.unknown()).optional(),
})
    .strict();
export const LayoutSpecEnvelopeSchema = z
    .object({
    specVersion: z.literal(LAYOUT_SPEC_VERSION),
    /** exact @json-render/* version the spec was authored against (adoption discipline) */
    jsonRenderVersion: z.string().min(1),
    /**
     * The raw json-render layout spec. Deliberately opaque here — structural
     * validation is json-render's `validateSpec(catalog, spec)` at the write
     * boundary. Kept as an object so the envelope round-trips through Postgres
     * jsonb without loss.
     */
    spec: z.record(z.string(), z.unknown()),
    /** section-semantics sidecar, keyed by section id used in `spec` */
    sections: z.array(SectionSemanticsSchema).default([]),
})
    .strict();
